import React from 'react';
import PropTypes from 'prop-types';
import has from 'lodash/has';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import noop from 'lodash/noop';
import { connect } from 'react-redux';
import { formatUrl } from 'url-lib';

import { constants, event, types } from '@eb/event-create-publish-utils';
import { SalesStatusForm } from '@eb/event-create-sales-status-form';
import { getWindowObject } from '@eb/feature-detection';
import { getLogoInfoUrl, getNonUserQuickLinks } from '@eb/site-header';

import { Dialog } from '@eb/eds-dialog';
import { resolveFormHooks } from '@eb/eds-form';
import { GlobalHeader } from '@eb/eds-global-header';
import { BlueContentIllustration } from '@eb/eds-iconography';
import { BlueContentMobileIllustration } from '@eb/eds-iconography';
import { Receipt } from '@eb/eds-iconography';
import { Trash } from '@eb/eds-iconography';
import { Illustration } from '@eb/eds-illustration';
import { Link } from '@eb/eds-link';
import { Modal } from '@eb/eds-modal';
import { Radio } from '@eb/eds-radio';
import { withOverlayControls } from '@eb/eds-structure';
import { trackAnalyticsEvent } from '@eb/site-analytics';

import gettext from '@eb/gettext';
import { lazyGettext } from '@eb/lazy-gettext';
import { openInNewTab } from '@eb/http';
import {
    getCoyoteHelpMenu,
    getOrganizerLogoInfo,
    getOverflowMenu,
    getPrimaryActionFromErrorDetail,
    getSecondaryActionFromErrorDetail,
    getUserMenuInfo,
    isEventDashboard,
} from '../../utils';
import LoadingScreen from '../LoadingScreen';
import { RaisedLoadingOverlay } from './RaisedLoadingOverlay';

import {
    ENV_SHAPE_PROP_TYPE,
    REQUEST_SHAPE_PROP_TYPE,
    USER_SHAPE_PROP_TYPE,
} from '@eb/site-structure';
import { STYLE_FILL } from '@eb/eds-button';
import { STYLE_ORGANIZER } from '@eb/eds-global-header';

const {
    SCHEDULE_PUBLISH_EVENT_INTENT,
    PUBLISH_EVENT_INTENT,
    CANCEL_SCHEDULE_EVENT_INTENT,
    CANCEL_DELETE_OPTIONS_DATA,
    CANCEL_DELETE_RADIO_GROUP_DEFAULT,
    CANCEL_MODAL_POSTPONE_CHOICE,
    CANCEL_MODAL_CANCEL_CHOICE,
    CANCEL_MODAL_DELETE_CHOICE,
    CHANGE_SALES_STATUS_URL_PARAM,
    DIALOG_POWERED_BY_FACEBOOK_MESSAGE,
    DIALOG_POWERED_BY_FACEBOOK_PRIMARY_TEXT,
    ERROR_CANCEL_REFUND,
    ERROR_CANCEL_REFUND_WITH_BULK_ENABLED,
    EVENT_DASHBOARD_BASE,
    EVENT_STATUS_BUTTON_UNPUBLISHED,
    EVENT_STATUS_BUTTON_PUBLISH_SCHEDULED,
    EVENT_STATUS_BUTTON_PUBLISHED,
    EVENT_STATUS_BUTTON_CANCELLED,
    EVENT_STATUS_BUTTON_ENDED,
    CANCEL_EVENT_URL_PARAM,
    SCHEDULED_PUBLISH_URL_PARAM,
    UNSCHEDULE_PUBLISH_URL_PARAM,
    UNSCHEDULE_MODAL_COPY,
    ACTION_URL_PARAM,
    LINK_FACEBOOK_EVENT,
    GA_ACTION_PROFILE_DROPDOWN,
    GA_CATEGORY_HEADER,
    GA_LABEL_CREATOR_EVENT,
} = constants;

const { FORM_HOOKS_PROPTYPE, EVENT_STATUS_BUTTON_TYPES } = types;
const {
    api: { getEventActionErrorDetail },
    components: {
        getCanceledEventActionMenu,
        getEndedEventActionMenu,
        getEventStatusMenuItem,
        getPreviewEventLinks,
        getPublishedEventActionsMenu,
        getSchedulePublishEventActionsMenu,
        getUnpublishedEventActionsMenu,
        getViewEventLinks,
    },
} = event;

const CancelDeleteRadioOption = ({ headline, description }) => (
    <div className="eds-l-mar-bot-6">
        <h3 className="eds-text-bm eds-text-color--grey-800">{headline}</h3>
        <p className="eds-text-bs eds-text-color--grey-600">{description}</p>
    </div>
);

// TODO: Translate the text copy once it's final
const CancelDeleteRadioOptions = CANCEL_DELETE_OPTIONS_DATA.map((data) => ({
    value: data.value,

    display: (
        <CancelDeleteRadioOption
            headline={data.headline}
            description={data.description}
        />
    ),
}));

const CancelModalContent = ({ onChange }) => (
    <section>
        <Radio
            id="cancel-delete-event-radio-group"
            name="cancel-delete-event"
            values={CancelDeleteRadioOptions}
            onChange={onChange}
            defaultValue={CANCEL_DELETE_RADIO_GROUP_DEFAULT}
        />
    </section>
);

const PostponeModalContent = ({ onChange }) => (
    <section>
        <p className="eds-text-bl eds-text-color--grey-800">
            {gettext(
                'Before postponing your event, remember these things to ensure the smoothest process for you and your attendees:',
            )}
        </p>
        <ul className="eds-l-mar-top-6 eds-l-pad-left-4">
            <li>
                {gettext(
                    "Email your attendees to let them know you're postponing your event.",
                )}
            </li>
            <li>
                {gettext(
                    'Consider issuing refunds as your attendees might not be able to make a new date.',
                )}
            </li>
            <li>
                {gettext(
                    'When you finalize your new date, change your date and time, and update your ticket sales end date to allow attendees to purchase tickets again.',
                )}
                <span>
                    <Link
                        to="/support/articleredirect?anum=34497"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {' '}
                        {gettext('Learn more')}
                    </Link>
                </span>
            </li>
        </ul>
    </section>
);

const UnschedulePublishDialog = ({
    isShown,
    onClose,
    onCancelSchedulePublishClick,
}) => (
    <Dialog
        isShown={isShown}
        headerIconType={<Trash />}
        message={UNSCHEDULE_MODAL_COPY}
        title={gettext('Unschedule')}
        onClose={onClose}
        onPrimaryAction={onCancelSchedulePublishClick}
        primaryText={gettext('Unschedule')}
        primaryType="link"
        onSecondaryAction={onClose}
        secondaryText={gettext('Cancel')}
        secondaryType="link"
        onClickOutside={onClose}
    />
);

const ConnectedOrganizerHeaderLoadingOverlay = connect((state) => {
    const eventStatusList = [
        'unpublishLoading',
        'cancelLoading',
        'deleteLoading',
        'saveSalesStatusLoading',
    ];

    return {
        isShown:
            state.eventStatus &&
            eventStatusList.some((isLoadingFlag) =>
                Boolean(state.eventStatus[isLoadingFlag]),
            ),
    };
})(RaisedLoadingOverlay);

const loadingComponentText = lazyGettext('Finalizing event details...');

const ConnectedLoadingComponent = connect(
    ({ eventStatus: { publishLoading = false } = {} }) => ({
        isShown: publishLoading,
    }),
)(LoadingScreen);

class OrganizerHeader extends React.Component {
    static propTypes = {
        env: ENV_SHAPE_PROP_TYPE.isRequired,
        request: REQUEST_SHAPE_PROP_TYPE.isRequired,
        user: USER_SHAPE_PROP_TYPE.isRequired,
        navigateToUrl: PropTypes.func.isRequired,
        eventId: PropTypes.string,
        baseUrl: PropTypes.string,
        eventStatusButtonType: PropTypes.oneOf(EVENT_STATUS_BUTTON_TYPES),
        eventStatusData: PropTypes.shape({
            name: PropTypes.string,
        }),
        onPublishClick: PropTypes.func,
        onUnpublishClick: PropTypes.func,
        onPostponeClick: PropTypes.func,
        onCancelClick: PropTypes.func,
        onDeleteClick: PropTypes.func,
        onCancelSchedulePublishClick: PropTypes.func,
        onSchedulePublishClick: PropTypes.func,
        eventActionError: PropTypes.string,
        onClearError: PropTypes.func,
        formHooks: FORM_HOOKS_PROPTYPE,
        hasPublish: PropTypes.bool,
        hasPaidPublish: PropTypes.bool,
        hasCoyotePublish: PropTypes.bool,
        hasCoyotePaidPublish: PropTypes.bool,
        isPaidEvent: PropTypes.bool,
        isScheduleEventPublishEnabled: PropTypes.bool,
        hideOrganizationSwitch: PropTypes.bool,
        SchedulePublishModalForm: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.func,
        ]),
        eventSalesStatus: PropTypes.shape({
            salesStatusCode: PropTypes.string,
            salesStatusMessage: PropTypes.string,
            salesStatusMessageType: PropTypes.string,
            salesStatusMessageCode: PropTypes.string,
            isNotYetOnSale: PropTypes.bool,
            isOnSale: PropTypes.bool,
            isSoldOut: PropTypes.bool,
            salesStartDate: PropTypes.shape({
                utc: PropTypes.datetime,
                local: PropTypes.datetime,
                timezone: PropTypes.string,
            }),
        }),
        trackGASelectPostponeStatusEvent: PropTypes.func,
        trackEvent: PropTypes.func,
        trackChangeSalesStatusEvent: PropTypes.func,
        hasPermissionToManageEventSalesStatus: PropTypes.bool,
        manageNewEventStatusFeatureEnabled: PropTypes.bool,
        launchRefundCreatorRefundProcessChannelStrategyV2: PropTypes.bool,
        onChangeSalesStatusClick: PropTypes.func,
        isPoweredByFb: PropTypes.bool,
        facebookEventId: PropTypes.number,
        isSeriesChild: PropTypes.bool,
        deployFollowerInsightsOrganization: PropTypes.bool,
        followerCount: PropTypes.number,
        hasPermissionToCopy: PropTypes.bool,
    };

    static defaultProps = {
        hasPermissionToManageEventSalesStatus: false,
        hasPermissionToCopy: false,
        onCancelClick: noop,
        onClearError: noop,
        onDeleteClick: noop,
        onUnpublishClick: noop,
        onPostponeClick: noop,
        onPublishClick: noop,
        onChangeSalesStatusClick: noop,
    };

    state = {
        showCancelModal: false,
        cancelModalChoice: CANCEL_DELETE_RADIO_GROUP_DEFAULT,
        confirmDialogProps: null,
        showSchedulePublishModal: false,
        showUnschedulePublishDialog: false,
        showChangeSalesStatusModal: false,
        showPostponeEventModal: false,
    };

    componentDidMount() {
        const { params, request } = this.props;
        const hasActionUrlParam = has(params, ACTION_URL_PARAM);
        const hasSchedulePublishInActionParam =
            hasActionUrlParam &&
            params[ACTION_URL_PARAM] === SCHEDULED_PUBLISH_URL_PARAM;
        const hasUnSchedulePublishInActionParam =
            hasActionUrlParam &&
            params[ACTION_URL_PARAM] === UNSCHEDULE_PUBLISH_URL_PARAM;
        const hasChangeSalesStatusInActionParam =
            hasActionUrlParam &&
            params[ACTION_URL_PARAM] === CHANGE_SALES_STATUS_URL_PARAM;
        const hasCancelEventInActionParam =
            hasActionUrlParam &&
            params[ACTION_URL_PARAM] === CANCEL_EVENT_URL_PARAM;
        const hasPostponeEventInActionParam =
            request && request.params && request.params.showPostponeModal;

        if (hasUnSchedulePublishInActionParam) {
            this._handleUnschedulePublishModal();
        } else if (hasSchedulePublishInActionParam) {
            this._handleOpenSchedulePublishModal();
        } else if (hasChangeSalesStatusInActionParam) {
            this._handleOpenChangeSalesStatusModal();
        } else if (hasCancelEventInActionParam) {
            this._resolveFormHooksThenShowCancelModal();
        } else if (hasPostponeEventInActionParam) {
            this._handleOpenPostponeEventModal();
        }
    }

    _resolveFormHooks() {
        const { formHooks } = this.props;

        const { handleSubmit, reset, dirty } = formHooks || {};

        if (!dirty) {
            return Promise.resolve();
        }

        const setDialogProps = (confirmDialogProps) =>
            this.setState({ confirmDialogProps });

        return resolveFormHooks(setDialogProps, handleSubmit, reset);
    }

    _resolveFormHooksThenPublish() {
        const { onPublishClick } = this.props;
        this._resolveFormHooks().then(() => onPublishClick());
    }

    _handleUnpublishedEventMenuSelect(value) {
        const { isManagePage } = this.props;

        if (value === PUBLISH_EVENT_INTENT) {
            this._resolveFormHooksThenPublish();
        } else if (value === SCHEDULE_PUBLISH_EVENT_INTENT) {
            // If manage page, redirect to Basic Info with modal open.
            // If Create page, open scheduled publish modal.
            if (isManagePage) {
                this._handleSchedulePublishOnManage();
            } else {
                this._handleOpenSchedulePublishModal();
            }
        }
    }

    _handleCloseUnschedulePublishModal() {
        this.setState({
            showUnschedulePublishDialog: false,
        });
    }

    _handleUnschedulePublishClick() {
        const { onCancelSchedulePublishClick } = this.props;

        onCancelSchedulePublishClick();
        this._handleCloseUnschedulePublishModal();
    }

    _handleSchedulePublishEventMenuSelect(value) {
        const { isManagePage } = this.props;
        if (value === PUBLISH_EVENT_INTENT) {
            this._resolveFormHooksThenPublish();
        } else if (value === CANCEL_SCHEDULE_EVENT_INTENT) {
            // If Manage app, redirect to Basic Info with cancel modal open.
            // If Create app, open scheduled publish modal.
            if (isManagePage) {
                this._handleSchedulePublishOnManage(true);
            } else {
                this._handleUnschedulePublishModal();
            }
        } else if (value.startsWith(SCHEDULE_PUBLISH_EVENT_INTENT)) {
            // If manage app, redirect to Basic Info with modal open.
            // If Create app, open scheduled publish modal.
            if (isManagePage) {
                this._handleSchedulePublishOnManage();
            } else {
                this._resolveFormHooks().then(() =>
                    this._handleOpenSchedulePublishModal(),
                );
            }
        }
    }

    _redirectPoweredByFacebook = () => {
        const { facebookEventId } = this.props;

        openInNewTab(`https://${LINK_FACEBOOK_EVENT}${facebookEventId}/`);
    };

    _dialogPoweredByFacebook() {
        const {
            eventStatusData: { name: eventName },
            showOverlay,
        } = this.props;

        showOverlay('dialog', {
            title: eventName,
            isShown: false,
            message: DIALOG_POWERED_BY_FACEBOOK_MESSAGE,
            primaryText: DIALOG_POWERED_BY_FACEBOOK_PRIMARY_TEXT,
            primaryType: STYLE_FILL,
            onPrimaryAction: this._redirectPoweredByFacebook,
            isClosable: true,
            onClose: () => ({}),
            illustration: (
                <div className="eds-example-modal__illustration__container eds-bg-color--white">
                    <div className="eds-example-modal__illustration__content--top-right">
                        <div className="eds-show-down-sw">
                            <Illustration
                                type={<BlueContentMobileIllustration />}
                                width="320px"
                                height="160px"
                            />
                        </div>
                        <div className="eds-show-up-mn">
                            <Illustration
                                type={<BlueContentIllustration />}
                                width="222px"
                                height="350px"
                            />
                        </div>
                    </div>
                </div>
            ),
        });
    }

    _resolveFormHooksThenUnpublish() {
        const { onUnpublishClick, eventId, isPoweredByFb } = this.props;

        if (isPoweredByFb) {
            this._dialogPoweredByFacebook();
        } else {
            this._resolveFormHooks().then(() => onUnpublishClick(eventId));
        }
    }

    _resolveFormHooksThenShowCancelModal() {
        const { isPoweredByFb } = this.props;

        if (isPoweredByFb) {
            this._dialogPoweredByFacebook();
        } else {
            this._resolveFormHooks().then(() =>
                this.setState({ showCancelModal: true }),
            );
        }
    }

    _resolveFormHooksThenConfirmDelete() {
        const { isPoweredByFb } = this.props;

        if (isPoweredByFb) {
            this._dialogPoweredByFacebook();
        } else {
            this._resolveFormHooks().then(() => {
                const { onDeleteClick, eventId } = this.props;
                const confirm = getWindowObject('confirm');

                // eslint-disable-next-line no-alert
                if (
                    confirm &&
                    confirm(
                        gettext(
                            'Are you sure you want to delete this event?  This cannot be undone.',
                        ),
                    )
                ) {
                    onDeleteClick(eventId);
                }
            });
        }
    }

    _handleCloseModal() {
        this.setState({
            showCancelModal: false,
        });
    }

    _handleCloseChangeStatusModal() {
        this.setState({
            showChangeSalesStatusModal: false,
        });
    }

    _handleClosePostponeEventModal() {
        this.setState({
            showPostponeEventModal: false,
        });
    }

    _handleCloseSchedulePublishModal() {
        this.setState({
            showSchedulePublishModal: false,
        });
    }

    _handleSchedulePublishRedirectToBasicInfo() {
        const { params, navigateToUrl, eventId } = this.props;
        const basicInfoUrl = `/manage/events/${eventId}/basicinfo/`;

        if (has(params, ACTION_URL_PARAM)) {
            navigateToUrl(basicInfoUrl);
        }
    }

    _handleOpenSchedulePublishModal() {
        // This will redirect the user back to basic info and remove params
        this._handleSchedulePublishRedirectToBasicInfo();

        this.setState({ showSchedulePublishModal: true });
    }

    _handleUnschedulePublishModal() {
        const {
            onCancelSchedulePublishClick,
            isScheduleEventPublishEnabled,
        } = this.props;

        // This will redirect the user back to basic info and remove params
        this._handleSchedulePublishRedirectToBasicInfo();

        if (isScheduleEventPublishEnabled) {
            this.setState({
                showUnschedulePublishDialog: true,
            });
        } else {
            this._resolveFormHooks().then(() => onCancelSchedulePublishClick());
        }
    }

    _handlePostponeClick() {
        const {
            onPostponeClick,
            isManagePage,
            request,
            trackChangeSalesStatusEvent,
        } = this.props;

        if (isManagePage && !isEventDashboard(request.path)) {
            this._handlePostponeOnManage();
        } else {
            trackChangeSalesStatusEvent && trackChangeSalesStatusEvent();
            onPostponeClick();
        }
    }

    _handleConfirmCancel() {
        this.setState({ showCancelModal: false });
        const { onCancelClick, onDeleteClick, eventId } = this.props;

        if (this.state.cancelModalChoice === CANCEL_MODAL_CANCEL_CHOICE) {
            onCancelClick();
        } else if (
            this.state.cancelModalChoice === CANCEL_MODAL_POSTPONE_CHOICE
        ) {
            this._handleOpenPostponeEventModal();
        } else if (
            this.state.cancelModalChoice === CANCEL_MODAL_DELETE_CHOICE
        ) {
            onDeleteClick(eventId);
        }
    }

    _handleCancelModalActionChange(value) {
        this.setState({ cancelModalChoice: value });
    }

    _handleCloseErrorDialog() {
        this.props.onClearError();
    }

    _handleNavigateToUrl(url) {
        const { navigateToUrl, onClearError } = this.props;

        onClearError();
        navigateToUrl(url);
    }

    _handleChangeSalesStatusClick() {
        const { isManagePage } = this.props;

        if (isManagePage) {
            this._handleChangeSalesStatusOnManage();
        } else {
            this._handleOpenChangeSalesStatusModal();
        }
    }

    _handleChangeSalesStatusRedirectToTickets() {
        const {
            navigateToUrl,
            eventId,
            trackChangeSalesStatusEvent,
        } = this.props;
        const ticketsUrl = `/manage/events/${eventId}/tickets/`;

        trackChangeSalesStatusEvent();

        navigateToUrl(ticketsUrl);
    }

    _handleOpenChangeSalesStatusModal() {
        // This will redirect the user back to tickets and remove params
        this._handleChangeSalesStatusRedirectToTickets();

        this.setState({
            showChangeSalesStatusModal: true,
        });
    }

    _handleOpenPostponeEventModal() {
        const {
            isManagePage,
            request,
            trackGASelectPostponeStatusEvent,
        } = this.props;

        if (isManagePage && !isEventDashboard(request.path)) {
            // Redirect the request to event dashboard and open the postpone modal
            this._handlePostponeOnManage();
        } else {
            if (trackGASelectPostponeStatusEvent) {
                const buttonDimension = request?.params?.source;
                trackGASelectPostponeStatusEvent(buttonDimension);
            }

            this.setState({ showPostponeEventModal: true });
        }
    }

    _handleChangeSalesStatusOnManage() {
        const { navigateToUrl, eventId } = this.props;
        const ticketsUrl = `/manage/events/${eventId}/tickets/actions/`;
        const ticketsUrlWithParam = `${ticketsUrl}${CHANGE_SALES_STATUS_URL_PARAM}/`;

        navigateToUrl(ticketsUrlWithParam);
    }

    _handlePostponeOnManage() {
        const { navigateToUrl, eventId } = this.props;
        const eventDashboardUrl = formatUrl(EVENT_DASHBOARD_BASE, {
            eid: eventId,
            showPostponeModal: true,
        });

        navigateToUrl(eventDashboardUrl);
    }

    _handleSchedulePublishOnManage(isCancel) {
        const { navigateToUrl, eventId } = this.props;
        const basicInfoUrl = `/manage/events/${eventId}/basicinfo/`;
        // Since we can't grab the Scheduled Publish Form from the Create app,
        // we decided to redirect the user to Basic Info with the Scheduled Publish
        // modal opened. This is for V1 MVP. Future work: EB-97589
        let basicInfoUrlWithParam = `${basicInfoUrl}${SCHEDULED_PUBLISH_URL_PARAM}/`;

        if (isCancel) {
            basicInfoUrlWithParam = `${basicInfoUrl}${UNSCHEDULE_PUBLISH_URL_PARAM}/`;
        }

        navigateToUrl(basicInfoUrlWithParam);
    }

    _getSalesStatusModal() {
        const { params, onChangeSalesStatusClick } = this.props;
        const { showChangeSalesStatusModal } = this.state;

        return (
            <SalesStatusForm
                params={params}
                onSubmit={onChangeSalesStatusClick}
                onClose={this._handleCloseChangeStatusModal.bind(this)}
                shouldShowModal={showChangeSalesStatusModal}
            />
        );
    }

    _getPostponeEventModal() {
        const { showPostponeEventModal } = this.state;

        return (
            <Modal
                isShown={showPostponeEventModal}
                title={gettext('A few reminders...')}
                onPrimaryAction={this._handlePostponeClick.bind(this)}
                primaryText={gettext('Postpone event')}
                primaryType="fill"
                onClose={this._handleClosePostponeEventModal.bind(this)}
                onClickOutside={this._handleClosePostponeEventModal.bind(this)}
                secondaryText={gettext('Cancel')}
                onSecondaryAction={this._handleClosePostponeEventModal.bind(
                    this,
                )}
                secondaryType="link"
            >
                <PostponeModalContent />
            </Modal>
        );
    }

    getErrorDialog = () => {
        const {
            eventActionError,
            baseUrl,
            eventId,
            launchRefundCreatorRefundProcessChannelStrategyV2,
        } = this.props;
        // [TECH-DEBT] - EB-139434: This will be deleted once the bulk refunds rampup is done
        let newEventActionError = eventActionError;
        if (
            launchRefundCreatorRefundProcessChannelStrategyV2 &&
            eventActionError === ERROR_CANCEL_REFUND
        ) {
            newEventActionError = ERROR_CANCEL_REFUND_WITH_BULK_ENABLED;
        }

        const eventActionErrorDetail = getEventActionErrorDetail(
            newEventActionError,
        );

        const headerIconType = eventActionErrorDetail.icon || <Receipt />;
        const secondaryAction = getSecondaryActionFromErrorDetail(
            this._handleNavigateToUrl.bind(this),
            eventActionErrorDetail,
            baseUrl,
            eventId,
        );
        const body = eventActionErrorDetail.body({ eventId });
        const primaryAction = getPrimaryActionFromErrorDetail(
            this._handleNavigateToUrl.bind(this),
            eventActionErrorDetail,
            baseUrl,
            eventId,
        ) || {
            primaryText: gettext('Ok'),
            primaryType: 'link',
            onPrimaryAction: this._handleCloseErrorDialog.bind(this),
        };

        let errorMessage = body;

        if (eventActionErrorDetail.bodyContainsHtml) {
            errorMessage = (
                <span
                    className="eds-g-cell eds-g-cell-lg-1-1 eds-l-pad-vert-2 eds-text-weight--regular"
                    dangerouslySetInnerHTML={{
                        __html: body,
                    }}
                />
            );
        }

        return (
            <Dialog
                isShown={true}
                title={eventActionErrorDetail.title}
                message={errorMessage}
                headerIconType={headerIconType}
                onClose={this._handleCloseErrorDialog.bind(this)}
                onClickOutside={this._handleCloseErrorDialog.bind(this)}
                {...primaryAction}
                {...secondaryAction}
            />
        );
    };

    render() {
        const {
            env,
            request,
            user,
            params,
            eventId,
            eventStatusButtonType,
            eventActionError,
            baseUrl,
            isManagePage,
            isScheduleEventPublishEnabled,
            organization,
            hideOrganizationSwitch,
            SchedulePublishModalForm,
            schedulePublishDate,
            schedulePublishTime,
            onSchedulePublishClick,
            eventTimeZone,
            hasPermissionToManageEventSalesStatus,
            manageNewEventStatusFeatureEnabled,
            eventSalesStatus,
            isSeriesChild,
            deployFollowerInsightsOrganization,
            followerCount,
            hasPermissionToCopy,
            featureFlags,
            trackEvent,
        } = this.props;

        const {
            serverUrl,
            loginUrl,
            helpUrls,
            localeInfo: { locale },
        } = env;
        const { confirmDialogProps } = this.state;
        const { path: requestPath } = request;
        const { isAuthenticated, isSuperCreator } = user;
        let eventQuickLinks;
        const helpMenu = getCoyoteHelpMenu(helpUrls.home, locale);
        let additionalMenus = [helpMenu];
        let eventActionsMenu;
        let onAdditionalMenusSelect;
        let schedulePublishModal;
        let unschedulePublishDialog;
        let eventActionErrorDialog;
        let confirmPublishDialog;
        const salesStatusModal = this._getSalesStatusModal();
        const postponeEventModal = this._getPostponeEventModal();
        const showCancel = !includes(
            [EVENT_STATUS_BUTTON_CANCELLED, EVENT_STATUS_BUTTON_ENDED],
            eventStatusButtonType,
        );
        const showDelete = includes(
            [EVENT_STATUS_BUTTON_CANCELLED, EVENT_STATUS_BUTTON_ENDED],
            eventStatusButtonType,
        );
        const showCopy = !isSeriesChild && hasPermissionToCopy;
        const overflowMenuOptions = {
            baseUrl,
            eventId,
            isManagePage,
            showCopy,
            showCancel,
            showDelete,
            locale,
            onCancelClick: this._resolveFormHooksThenShowCancelModal.bind(this),
            onDeleteClick: this._resolveFormHooksThenConfirmDelete.bind(this),
        };

        if (!isEmpty(confirmDialogProps)) {
            confirmPublishDialog = (
                <Dialog isShown={true} {...confirmDialogProps} />
            );
        }

        if (eventActionError) {
            eventActionErrorDialog = this.getErrorDialog();
        }

        if (eventStatusButtonType === EVENT_STATUS_BUTTON_ENDED) {
            eventActionsMenu = getEndedEventActionMenu({
                serverUrl,
            });
            eventQuickLinks = getViewEventLinks(serverUrl, eventId, trackEvent);
        } else if (eventStatusButtonType === EVENT_STATUS_BUTTON_CANCELLED) {
            eventActionsMenu = getCanceledEventActionMenu(serverUrl);
            eventQuickLinks = getViewEventLinks(serverUrl, eventId, trackEvent);
        } else if (
            eventStatusButtonType === EVENT_STATUS_BUTTON_PUBLISH_SCHEDULED
        ) {
            eventQuickLinks = getPreviewEventLinks(
                serverUrl,
                eventId,
                trackEvent,
            );
            eventActionsMenu = getSchedulePublishEventActionsMenu(
                serverUrl,
                isScheduleEventPublishEnabled,
                schedulePublishDate,
                schedulePublishTime,
                eventTimeZone,
                locale,
            );
            onAdditionalMenusSelect = this._handleSchedulePublishEventMenuSelect.bind(
                this,
            );
        } else if (eventStatusButtonType === EVENT_STATUS_BUTTON_UNPUBLISHED) {
            eventQuickLinks = getPreviewEventLinks(
                serverUrl,
                eventId,
                trackEvent,
            );

            // Hide publish event actions from series children events.
            // Still need to show the `More` helper menu.
            if (isSeriesChild) {
                additionalMenus = [
                    getOverflowMenu(helpUrls, overflowMenuOptions),
                ];
            } else {
                eventActionsMenu = getUnpublishedEventActionsMenu(
                    serverUrl,
                    isScheduleEventPublishEnabled,
                );
                onAdditionalMenusSelect = this._handleUnpublishedEventMenuSelect.bind(
                    this,
                );
            }
        } else if (eventStatusButtonType === EVENT_STATUS_BUTTON_PUBLISHED) {
            eventQuickLinks = getViewEventLinks(serverUrl, eventId, trackEvent);

            // Show on sale status for series children but no action menu.
            if (isSeriesChild) {
                eventActionsMenu = getEventStatusMenuItem({
                    serverUrl,
                    eventSalesStatus,
                    locale,
                });
            } else {
                eventActionsMenu = getPublishedEventActionsMenu({
                    serverUrl,
                    onUnpublishClick: this._resolveFormHooksThenUnpublish.bind(
                        this,
                    ),
                    onChangeSalesStatusClick: this._handleChangeSalesStatusClick.bind(
                        this,
                    ),
                    manageNewEventStatusFeatureEnabled,
                    eventSalesStatus,
                    hasPermissionToManageEventSalesStatus,
                    locale,
                });
            }
        }

        if (SchedulePublishModalForm) {
            // only put the schedule publish modal form on the page if it should
            // be shown.  this will prevent a lot of additional processing for
            // the form and fixes an issue with validators being run before the
            // form is initialized.
            schedulePublishModal = (
                <SchedulePublishModalForm
                    params={params}
                    schedulePublishDate={schedulePublishDate}
                    schedulePublishTime={schedulePublishTime}
                    onSubmit={onSchedulePublishClick}
                    onClose={this._handleCloseSchedulePublishModal.bind(this)}
                    shouldShowModal={this.state.showSchedulePublishModal}
                />
            );

            unschedulePublishDialog = (
                <UnschedulePublishDialog
                    isShown={this.state.showUnschedulePublishDialog}
                    onClose={this._handleCloseUnschedulePublishModal.bind(this)}
                    onCancelSchedulePublishClick={this._handleUnschedulePublishClick.bind(
                        this,
                    )}
                />
            );
        }

        const eventCancelModal = (
            <Modal
                isShown={this.state.showCancelModal}
                title={gettext('Cancel Event')}
                onClose={this._handleCloseModal.bind(this)}
                onPrimaryAction={this._handleConfirmCancel.bind(this)}
                primaryText={gettext('Confirm')}
                primaryType="link"
                onSecondaryAction={this._handleCloseModal.bind(this)}
                secondaryText={gettext('Cancel')}
                secondaryType="link"
            >
                <CancelModalContent
                    onChange={this._handleCancelModalActionChange.bind(this)}
                />
            </Modal>
        );

        if (eventActionsMenu) {
            additionalMenus = [
                eventActionsMenu,
                getOverflowMenu(helpUrls, overflowMenuOptions),
            ];
        }

        const loadingScreen = (
            <ConnectedLoadingComponent text={loadingComponentText} />
        );
        const onTrackingUserDropdown = () => {
            trackAnalyticsEvent({
                action: GA_ACTION_PROFILE_DROPDOWN,
                category: GA_CATEGORY_HEADER,
                label: GA_LABEL_CREATOR_EVENT,
            });
        };
        return (
            <div>
                <GlobalHeader
                    onUserDropdownMouseEnter={onTrackingUserDropdown}
                    logoInfo={getOrganizerLogoInfo({
                        url: getLogoInfoUrl(user, serverUrl, requestPath),
                        requestPath,
                    })}
                    userMenuInfo={getUserMenuInfo({
                        env,
                        user,
                        organization,
                        hideOrganizationSwitch,
                        onlyUseAnchorLinks: true,
                        deployFollowerInsightsOrganization,
                        followerCount,
                        featureFlags,
                    })}
                    nonUserQuickLinks={getNonUserQuickLinks(
                        requestPath,
                        isAuthenticated,
                        loginUrl,
                    )}
                    quickLinks={eventQuickLinks}
                    additionalMenus={additionalMenus}
                    onAdditionalMenuTextItemSelect={onAdditionalMenusSelect}
                    style={STYLE_ORGANIZER}
                />
                {schedulePublishModal}
                {unschedulePublishDialog}
                {confirmPublishDialog}
                {eventCancelModal}
                {eventActionErrorDialog}
                {salesStatusModal}
                {postponeEventModal}
                <ConnectedOrganizerHeaderLoadingOverlay />
                {loadingScreen}
            </div>
        );
    }
}

export default withOverlayControls(OrganizerHeader);
